import { useCallback } from 'react';
import { useUserInfo } from '@/provider/UserInfoProvider';
import { posthogUTM } from '@/posthog';
import { RPostHog } from '@/posthog/report';
import { type EventMap } from '@/posthog/constants';

const { utm } = posthogUTM();
export default function useReportPoints() {
  const { userInfo, userIdentity } = useUserInfo();

  // 批量图像修复 - 用户批量选择图像时
  const batch_RP_after_select = useCallback(
    ({
      before_counts,
      single_counts,
      over_limit_counts,
    }: Pick<
      EventMap['batch_restore_after_select'],
      'before_counts' | 'single_counts' | 'over_limit_counts'
    >) => {
      RPostHog('batch_restore_after_select', {
        user_id: userInfo?.user_id || 'not_get_user_id',
        email:
          userIdentity === 2 ? 'anonymous' : userInfo?.email || 'not_get_email',
        user_identity: userIdentity,
        before_counts,
        single_counts,
        over_limit_counts,
        ...utm,
      });
    },
    [userIdentity, userInfo?.email, userInfo?.user_id]
  );

  // 批量图像修复 - 用户点击添加"+"更多图像时
  const batch_RP_after_click_add_more = useCallback(
    ({
      whichTimes,
    }: Pick<EventMap['batch_restore_after_click_add_more'], 'whichTimes'>) => {
      RPostHog('batch_restore_after_click_add_more', {
        user_id: userInfo?.user_id || 'not_get_user_id',
        email:
          userIdentity === 2 ? 'anonymous' : userInfo?.email || 'not_get_email',
        user_identity: userIdentity,
        whichTimes,
        ...utm,
      });
    },
    [userIdentity, userInfo?.email, userInfo?.user_id]
  );

  // 批量图像修复 - 用户点击「Repair Image」按钮
  const batch_RP_click_operate_btn = useCallback(
    ({
      all_counts,
      could_repair_counts,
      could_repair_images,
      repair_mode,
    }: Pick<
      EventMap['batch_restore_click_restore_image_btn'],
      | 'all_counts'
      | 'could_repair_counts'
      | 'could_repair_images'
      | 'repair_mode'
    >) => {
      RPostHog('batch_restore_click_restore_image_btn', {
        user_id: userInfo?.user_id || 'not_get_user_id',
        email:
          userIdentity === 2 ? 'anonymous' : userInfo?.email || 'not_get_email',
        user_identity: userIdentity,
        all_counts,
        could_repair_counts,
        could_repair_images,
        repair_mode,
        ...utm,
      });
    },
    [userIdentity, userInfo?.email, userInfo?.user_id]
  );

  // 批量图像修复 - 积分不足提示
  const batch_RP_show_no_enough_credits = useCallback(
    ({
      remain_credits,
      need_credits,
    }: Pick<
      EventMap['batch_restore_show_no_enough_credits'],
      'remain_credits' | 'need_credits'
    >) => {
      RPostHog('batch_restore_show_no_enough_credits', {
        user_id: userInfo?.user_id || 'not_get_user_id',
        email:
          userIdentity === 2 ? 'anonymous' : userInfo?.email || 'not_get_email',
        user_identity: userIdentity,
        remain_credits,
        need_credits,
        ...utm,
      });
    },
    [userIdentity, userInfo?.email, userInfo?.user_id]
  );

  // 批量图像修复 - 点击购买积分按钮
  const batch_RP_click_buy_credits_btn = useCallback(() => {
    RPostHog('batch_restore_click_buy_credits_btn', {
      user_id: userInfo?.user_id || 'not_get_user_id',
      email:
        userIdentity === 2 ? 'anonymous' : userInfo?.email || 'not_get_email',
      user_identity: userIdentity,
      ...utm,
    });
  }, [userIdentity, userInfo?.email, userInfo?.user_id]);

  // 批量图像修复 - 创建任务
  const batch_RP_after_create_tasks = useCallback(
    ({
      all_counts,
      could_repair_counts,
      could_repair_images,
      repair_mode,
      status,
      reason,
    }: Pick<
      EventMap['batch_restore_after_create_tasks'],
      | 'all_counts'
      | 'could_repair_counts'
      | 'could_repair_images'
      | 'repair_mode'
      | 'status'
      | 'reason'
    >) => {
      RPostHog('batch_restore_after_create_tasks', {
        user_id: userInfo?.user_id || 'not_get_user_id',
        email:
          userIdentity === 2 ? 'anonymous' : userInfo?.email || 'not_get_email',
        user_identity: userIdentity,
        ever_paid: userInfo?.ever_paid,
        all_counts,
        could_repair_counts,
        could_repair_images,
        repair_mode,
        status,
        reason,
        ...utm,
      });
    },
    [userIdentity, userInfo?.email, userInfo?.ever_paid, userInfo?.user_id]
  );

  // 批量图像修复 - 点击下载按钮
  const batch_RP_click_download = useCallback(
    ({
      download_way,
    }: Pick<EventMap['batch_restore_click_download'], 'download_way'>) => {
      RPostHog('batch_restore_click_download', {
        user_id: userInfo?.user_id || 'not_get_user_id',
        email:
          userIdentity === 2 ? 'anonymous' : userInfo?.email || 'not_get_email',
        user_identity: userIdentity,
        download_way,
        ...utm,
      });
    },
    [userIdentity, userInfo?.email, userInfo?.user_id]
  );

  // 批量图像修复 - 下载失败
  const batch_RP_after_download_fail = useCallback(
    ({
      reason,
      download_way,
    }: Pick<
      EventMap['batch_restore_after_download_fail'],
      'download_way' | 'reason'
    >) => {
      RPostHog('batch_restore_after_download_fail', {
        user_id: userInfo?.user_id || 'not_get_user_id',
        email:
          userIdentity === 2 ? 'anonymous' : userInfo?.email || 'not_get_email',
        user_identity: userIdentity,
        download_way,
        reason,
        ...utm,
      });
    },
    [userIdentity, userInfo?.email, userInfo?.user_id]
  );

  // 批量图像修复 - 点击我的项目
  const batch_RP_click_my_projects = useCallback(() => {
    RPostHog('batch_restore_click_my_projects', {
      user_id: userInfo?.user_id || 'not_get_user_id',
      email:
        userIdentity === 2 ? 'anonymous' : userInfo?.email || 'not_get_email',
      user_identity: userIdentity,
      ...utm,
    });
  }, [userIdentity, userInfo?.email, userInfo?.user_id]);

  // 批量图像修复 - 评分
  const batch_RP_rate = useCallback(
    ({
      rate,
      output_image_url,
    }: Pick<EventMap['batch_restore_rate'], 'rate' | 'output_image_url'>) => {
      RPostHog('batch_restore_rate', {
        user_id: userInfo?.user_id || 'not_get_user_id',
        email:
          userIdentity === 2 ? 'anonymous' : userInfo?.email || 'not_get_email',
        user_identity: userIdentity,
        rate,
        output_image_url,
        ...utm,
      });
    },
    [userIdentity, userInfo?.email, userInfo?.user_id]
  );

  // 批量图像修复 - 触发频控
  const batch_RP_Trigger_Rate_Limit = useCallback(() => {
    RPostHog('batch_restore_trigger_rate_limit', {
      user_id: userInfo?.user_id || 'not_get_user_id',
      email:
        userIdentity === 2 ? 'anonymous' : userInfo?.email || 'not_get_email',
      user_identity: userIdentity,
      ...utm,
    });
  }, [userIdentity, userInfo?.email, userInfo?.user_id]);

  // 批量图像修复 - 点击频控按钮
  const batch_RP_Upgrade_Rate_Limit = useCallback(() => {
    RPostHog('batch_restore_upgrade_rate_limit', {
      user_id: userInfo?.user_id || 'not_get_user_id',
      email:
        userIdentity === 2 ? 'anonymous' : userInfo?.email || 'not_get_email',
      user_identity: userIdentity,
      ...utm,
    });
  }, [userIdentity, userInfo?.email, userInfo?.user_id]);

  // 批量图像修复 - 点击unlock
  const batch_RP_Restore_Unlock_Btn = useCallback(
    ({
      from,
      trigger,
    }: Pick<
      EventMap['batch_restore_click_unlock_btn'],
      'from' | 'trigger'
    >) => {
      RPostHog('batch_restore_click_unlock_btn', {
        user_id: userInfo?.user_id || 'not_get_user_id',
        email:
          userIdentity === 2 ? 'anonymous' : userInfo?.email || 'not_get_email',
        user_identity: userIdentity,
        from,
        trigger,
        ...utm,
      });
    },
    [userIdentity, userInfo?.email, userInfo?.user_id]
  );

  return {
    batch_RP_after_select,
    batch_RP_after_click_add_more,
    batch_RP_click_operate_btn,
    batch_RP_show_no_enough_credits,
    batch_RP_click_buy_credits_btn,
    batch_RP_after_create_tasks,
    batch_RP_click_download,
    batch_RP_after_download_fail,
    batch_RP_click_my_projects,
    batch_RP_rate,
    batch_RP_Trigger_Rate_Limit,
    batch_RP_Upgrade_Rate_Limit,
    batch_RP_Restore_Unlock_Btn,
  };
}
